import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { PageHeader, Field, NativeSelect, LoadingBlock, Spinner } from "@/components/Shared";

const SECTIONS = [["footage", "Work inspected / operation (footage)"], ["welding", "Welding (counts)"], ["ndt", "NDT (counts)"]];
const EMPTY = { template: "pipeline", client_id: "", name: "", job_number: "", location: "", contractor: "", pipe_description: "", total_footage: 0, status: "active", start_date: "", end_date: "", notes: "", line_items: [], opening_values: {}, report_number_start: 1, assigned_employee_ids: [] };

function ItemRow({ item, onChange, onRemove, opening, setOpening, facility }) {
  const fac = facility && item.section === "footage";
  const pipeFt = !facility && item.section === "footage" && item.percent;
  return (
    <div className={`grid grid-cols-12 items-center gap-2 px-3 py-2 ${item.enabled ? "" : "opacity-55"}`} data-testid={`line-item-row-${item.key || item._tmp}`}>
      <div className="col-span-2 flex items-center sm:col-span-1">
        <Switch checked={item.enabled} onCheckedChange={(v) => onChange({ enabled: v })} data-testid={`line-item-enabled-${item.key || item._tmp}`} />
      </div>
      <input className={`field-input col-span-10 h-10 text-sm ${fac || pipeFt ? "sm:col-span-3" : "sm:col-span-5"}`} value={item.label} onChange={(e) => onChange({ label: e.target.value })} data-testid={`line-item-label-${item.key || item._tmp}`} />
      {fac ? (
        <>
          <input className="field-input col-span-5 h-10 text-sm sm:col-span-2" placeholder="Unit (Days)" value={item.unit || ""} onChange={(e) => onChange({ unit: e.target.value })} data-testid={`line-item-unit-${item.key || item._tmp}`} />
          <input type="number" min="0" step="any" placeholder="Est. (TBD)" title="Estimated completion footage / days" className="field-input col-span-5 h-10 text-right text-sm sm:col-span-2" value={item.estimate ?? ""} onChange={(e) => onChange({ estimate: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`line-item-estimate-${item.key || item._tmp}`} />
        </>
      ) : (
      <label className="col-span-5 flex items-center gap-2 text-xs font-semibold text-slate-600 sm:col-span-2">
        {item.section === "footage" ? (
          <><input type="checkbox" className="h-4 w-4 accent-[#5C241E]" checked={item.percent} onChange={(e) => onChange({ percent: e.target.checked })} />% complete</>
        ) : (
          <><input type="checkbox" className="h-4 w-4 accent-[#5C241E]" checked={item.in_total} onChange={(e) => onChange({ in_total: e.target.checked })} />In total</>
        )}
      </label>
      )}
      {pipeFt && (
        <input type="number" min="0" step="any" placeholder="Planned ft" title="Optional: planned footage for this item. When set, % complete = total ÷ planned instead of total pipeline footage" aria-label={`${item.label} planned footage`} className="field-input col-span-5 h-10 text-right text-sm sm:col-span-2" value={item.estimate ?? ""} onChange={(e) => onChange({ estimate: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`line-item-planned-${item.key || item._tmp}`} />
      )}
      <input type="number" min="0" step="any" placeholder="0" title="Carry-over (previous) before portal" className={`field-input col-span-5 h-10 text-right text-sm ${fac ? "sm:col-span-3" : "sm:col-span-3"}`}
        value={opening ?? ""} onChange={(e) => setOpening(e.target.value)} disabled={!item.key} data-testid={`line-item-opening-${item.key || item._tmp}`} />
      <button type="button" onClick={onRemove} className="col-span-2 flex h-10 items-center justify-center text-slate-400 hover:text-red-600 sm:col-span-1" aria-label="Remove item" data-testid={`line-item-remove-${item.key || item._tmp}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function ProjectForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [f, setF] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
      setClients(c.data);
      setUsers(u.data.filter((x) => x.role === "employee" && x.active));
      if (id) setF((await api.get(`/projects/${id}`)).data);
      else setF({ ...EMPTY, client_id: sp.get("client_id") || "", line_items: (await api.get("/line-items/defaults")).data });
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!f) return <LoadingBlock />;
  const set = (k, v) => setF({ ...f, [k]: v });
  const updItem = (idx, upd) => set("line_items", f.line_items.map((it, i) => (i === idx ? { ...it, ...upd } : it)));
  const addItem = (section) => set("line_items", [...f.line_items, { _tmp: `n${Date.now()}`, label: "New item", section, percent: false, in_total: false, enabled: true, unit: "", estimate: null }]);
  const facility = f.template === "facility";
  const changeTemplate = async (t) => {
    if (t === f.template) return;
    const replace = !id || window.confirm("Switch the line items to this template's default list? Your current item list will be replaced.");
    const items = replace ? (await api.get("/line-items/defaults", { params: { template: t } })).data : f.line_items;
    setF({ ...f, template: t, line_items: items });
  };
  const toggleEmp = (uid) => set("assigned_employee_ids", f.assigned_employee_ids?.includes(uid) ? f.assigned_employee_ids.filter((x) => x !== uid) : [...(f.assigned_employee_ids || []), uid]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.client_id) return toast.error("Select a client");
    setBusy(true);
    try {
      const body = { ...f, total_footage: Number(f.total_footage || 0), report_number_start: Number(f.report_number_start || 1),
        opening_values: Object.fromEntries(Object.entries(f.opening_values || {}).map(([k, v]) => [k, Number(v || 0)])),
        line_items: f.line_items.map(({ _tmp, ...it }) => it) };
      const { data } = id ? await api.put(`/projects/${id}`, body) : await api.post("/projects", body);
      toast.success(id ? "Project saved" : "Project created");
      if (!id) nav(`/admin/projects/${data.id}`, { replace: true }); else setF(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const inp = (k, props = {}) => <input className="field-input" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`project-${k}-input`} {...props} />;
  const enabledCount = f.line_items.filter((i) => i.enabled).length;

  return (
    <form onSubmit={save} className="fade-up pb-10">
      <Link to="/admin/projects" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-[#5C241E]" data-testid="project-back-link"><ArrowLeft className="h-4 w-4" />Projects</Link>
      <PageHeader eyebrow={id ? `Job # ${f.job_number || "—"}` : "New project"} title={id ? f.name : "Add project"} actions={id && <>
        <Link to={`/admin/reports?project_id=${id}`} className="btn-ghost h-11" data-testid="project-view-reports"><FileText className="h-4 w-4" />Reports</Link>
        <button type="button" className="btn-ghost h-11" onClick={() => downloadFile(`/exports/project/${id}/workbook`).catch((e) => toast.error(errMsg(e)))} data-testid="project-export-workbook"><FileSpreadsheet className="h-4 w-4" />Daily workbook (.xlsx)</button>
      </>} />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="panel space-y-4 p-5 xl:col-span-1">
          <p className="font-display text-xl font-semibold">Project details</p>
          <Field label="Client" required>
            <NativeSelect value={f.client_id} onChange={(e) => set("client_id", e.target.value)} data-testid="project-client-input">
              <option value="">Select client…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Report format" hint="Pipeline uses total pipeline footage; facility uses a unit + estimate per item">
            <NativeSelect value={f.template || "pipeline"} onChange={(e) => changeTemplate(e.target.value)} data-testid="project-template-input">
              <option value="pipeline">Pipeline (footage)</option><option value="facility">Facility / construction (days & footage)</option>
            </NativeSelect>
          </Field>
          <Field label="Project name" required>{inp("name", { required: true })}</Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={facility ? "Job AFE#" : "Job #"}>{inp("job_number")}</Field>
            <Field label="Status">
              <NativeSelect value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="project-status-input">
                <option value="active">Active</option><option value="on_hold">On hold</option><option value="completed">Completed</option><option value="archived">Archived</option>
              </NativeSelect>
            </Field>
          </div>
          <Field label="Location">{inp("location")}</Field>
          <Field label="Contractor" hint="Pre-fills on reports">{inp("contractor")}</Field>
          <Field label={facility ? "Job description" : "Pipe description"} hint={facility ? "e.g. Site Expansion to OGT" : 'e.g. 20" Steel'}>{inp("pipe_description")}</Field>
          {!facility && <Field label="Total footage of whole pipeline" hint="Used for percent complete">{inp("total_footage", { type: "number", min: 0, step: "any" })}</Field>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">{inp("start_date", { type: "date" })}</Field>
            <Field label="End date">{inp("end_date", { type: "date" })}</Field>
          </div>
          <Field label="First report number" hint="Use if this job already has paper reports">{inp("report_number_start", { type: "number", min: 1 })}</Field>
          <Field label="Notes"><textarea rows={3} className="field-area" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="project-notes-input" /></Field>
          <div>
            <p className="mb-1 text-sm font-semibold">Assigned employees</p>
            <p className="mb-2 text-xs text-slate-500">Leave all unchecked to let every employee report on this project.</p>
            <div className="max-h-48 space-y-1 overflow-auto rounded-md border border-border p-2" data-testid="project-assign-list">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-[#5C241E]" checked={f.assigned_employee_ids?.includes(u.id) || false} onChange={() => toggleEmp(u.id)} data-testid={`project-assign-${u.id}`} />{u.name}</label>
              ))}
              {!users.length && <p className="text-xs text-slate-500">No active employees yet.</p>}
            </div>
          </div>
        </div>

        <div className="panel xl:col-span-2" data-testid="project-line-items">
          <div className="border-b border-border p-5">
            <p className="font-display text-xl font-semibold">Report line items <span className="text-base font-normal text-slate-500">({enabledCount} selected)</span></p>
            <p className="mt-1 text-sm text-slate-500">Switch on the items inspectors should fill in for this job. They print on the daily report in this order. "Carry-over" is the previous total from before the portal (optional).</p>
          </div>
          {SECTIONS.map(([key, title]) => (
            <div key={key} className="border-b border-border last:border-0">
              <div className="flex items-center justify-between bg-[#FAC090]/60 px-3 py-2">
                <p className="text-xs font-bold uppercase tracking-wider">{title}</p>
                <span className="hidden text-[10px] font-bold uppercase tracking-wider text-slate-600 sm:block">Carry-over</span>
              </div>
              <div className="divide-y divide-border">
                {f.line_items.map((it, idx) => it.section === key && (
                  <ItemRow key={it.key || it._tmp} item={it} facility={facility} onChange={(u) => updItem(idx, u)} onRemove={() => set("line_items", f.line_items.filter((_, i) => i !== idx))}
                    opening={f.opening_values?.[it.key]} setOpening={(v) => set("opening_values", { ...f.opening_values, [it.key]: v })} />
                ))}
              </div>
              <button type="button" onClick={() => addItem(key)} className="m-3 inline-flex items-center gap-1 text-sm font-semibold text-[#5C241E]" data-testid={`add-line-item-${key}`}><Plus className="h-4 w-4" />Add custom item</button>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button className="btn-brand h-12 w-full sm:w-auto" disabled={busy} data-testid="project-save-button">{busy && <Spinner className="text-white" />}{id ? "Save project" : "Create project"}</button>
      </div>
    </form>
  );
}
